<template>
  <div style="clear: both">
    <v-dialog v-model="loading" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t('Loading') }}...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-card>
      <v-toolbar elevation="1" density="compact">
        <v-toolbar-title>
          <span>{{ $t('Discussion') }} ({{ loadedComments.length }})</span>
          <v-tooltip :text="showDiscussion ? $t('Hide') : $t('Show')" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn icon @click="showDiscussion = !showDiscussion" v-bind="props">
                  <v-icon>{{ showDiscussion ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                </v-btn>
            </template>
          </v-tooltip>
        </v-toolbar-title>
      </v-toolbar>
      <v-container fluid ref="commentContainer" v-if="showDiscussion">
        <v-row justify="start">
          <v-col cols="12" sm="12">
            <ul class="comments">
              <li :id="item.id" v-for="item in comments" :key="item.id" class="px-3">
                <div>
                  <pre class="comment">{{ item.commentText }}</pre>
                  <span class="author">
                    <a v-if="!readonly" href="#" @click.prevent="reply(item)" class="reply-link mr-2">{{ $t('Reply') }}</a>
                    {{ item.insertedBy.displayName }} - {{ $filters.DateTimeFilter(item.insertedByDate) }}
                  </span>
                </div>
                <ul class="comments replied" v-if="item.comments && item.comments.length">
                  <li v-for="reply in item.comments" :key="reply.id">
                    <div>
                      <pre class="comment">{{ reply.commentText }}</pre>
                      <span class="author">{{ reply.insertedBy.displayName }} - {{ $filters.DateTimeFilter(reply.insertedByDate) }}</span>
                    </div>
                  </li>
                </ul>
              </li>
            </ul>
          </v-col>
        </v-row>
      </v-container>
      <template v-if="!readonly && showDiscussion">
        <v-form ref="discussionForm" v-model="commentValid" autocomplete="off">
          <v-row dense>
            <v-col cols="12" sm="12">
              <h4 class="ml-2" v-if="replyTo">{{ $t('Replying_To') }}:</h4>
              <div v-if="replyTo" :class="{ reply: replyTo }">
                <div class="selected-reply">
                  <pre class="comment">{{ replyTo.commentText }}</pre>
                  <span class="author">{{ replyTo.insertedBy.displayName }} - {{ $filters.DateTimeFilter(replyTo.insertedByDate) }}</span>
                </div>
                <v-tooltip :text="$t('Cancel_Reply')" location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" @click="cancelReply" class="cancel-reply-button">mdi-close</v-icon>
                  </template>
                </v-tooltip>
              </div>
              <v-textarea
                :label="$t('New_Comment')"
                name="newcomment"
                v-model="newcomment"
                ref="comment"
                required
                auto-grow
                rows="1"
                class="mx-2"
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="12" style="display: flex;">
              <v-btn class="mx-2 mb-2" color="success" :disabled="newcomment.length === 0" @click="submitComment()">{{
                $t('Save_Comment')
              }}</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </v-card>
    <toaster ref="toaster"></toaster>
  </div>
</template>

<script>
import ExternalService from '@/services/external.service'
import Toaster from '@/components/Toaster'
export default {
  name: 'Comments',
  data() {
    return {
      loadedComments: [],
      selectedCommunityShortName: null,
      newcomment: '',
      commentValid: false,
      replyTo: undefined,
      showDiscussion: this.initialDiscussionDisplay,
      loading: false
    }
  },
  props: {
    mocId: {
      type: String
    },
    communityAbbrev: {
      type: String
    },
    readonly: {
      type: Boolean,
      default: true
    },
    showToast: {
      type: Function,
      default: () => {}
    },
    initialDiscussionDisplay: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Toaster
  },
  computed: {
    rules() {
      return {
        length: len => v => (v || '').length >= len || this.$t('Rule_ExactLength', { length: len }),
        required: v => (v && v.trim() ? false : this.$t('Rule_Required'))
      }
    },
    comments() {
      return this.loadedComments
        .filter(c => !c.parentId)
        .reduce((acc, cur) => {
          acc[cur.id] = cur
          acc[cur.id].comments = this.mappedRepliedComments[cur.id]
            ? (acc[cur.id].comments || []).concat(this.mappedRepliedComments[cur.id])
            : []
          return acc
        }, {})
    },
    mappedRepliedComments() {
      return this.loadedComments.reduce((acc, cur) => {
        if (!cur.parentId) return acc
        if (!acc[cur.parentId]) acc[cur.parentId] = []
        acc[cur.parentId].push(cur)
        return acc
      }, {})
    }
  },
  methods: {
    getComments(mocId) {
      this.loading = true
      return ExternalService.getComments(mocId)
        .then(data => {
          this.loadedComments = data
        })
        .catch(() => {
          this.$refs.loadingDialog.error({ message: this.$t('Error_GetComments', { mocId: mocId }) })
        })
        .finally(() => (this.loading = false))
    },
    submitComment: function() {
      if (!this.commentValid) return
      this.selectedCommunityShortName = this.$route.params.shortName
      ExternalService.saveComment(this.mocId, {
        commentText: this.newcomment,
        ...(this.replyTo ? { parentId: this.replyTo.id } : {})
      })
        .then(() => {
          this.$refs.toaster.pop('success', this.$t('Success_CommentSaved'))
          this.newcomment = ''
          this.getComments(this.mocId)
        })
        .catch(e => {
          let message = e.response.status === 401 ? this.$t('Error_Unauthorized') : e.response.data.message
          this.$refs.toaster.pop('error', message)
        })

      this.replyTo = undefined
    },
    reply(comment) {
      this.replyTo = comment
      this.$refs.comment.focus()
    },
    cancelReply() {
      this.replyTo = undefined
    }
  },
  beforeMount() {
    this.selectedCommunityShortName = this.$route.params.shortName
    this.getComments(this.mocId)
  }
}
</script>

<style scoped>
ul {
  list-style-type: none;
}

li > div {
  display: block;
  padding: 2px;
  margin: 10px;

  border: 1px solid lightgrey;
  border-left: 3px solid lightslategray;
  word-wrap: break-word;
  white-space: normal;
}
.author {
  color: gray;
  text-align: right;
  display: block;
}
.comment {
  color: black;
}

.reply-link {
  text-decoration: underline;
}

pre {
  white-space: pre-wrap;
}

.reply {
  border: 1px solid lightgrey;
  border-left: 3px solid lightslategray;
  padding: 8px;
  margin: 4px;
  position: relative;
}

.replied > li > div {
  border-left: 3px solid#4169e1;
  margin-left: 7%;
}

.cancel-reply-button {
  position: absolute;
  right: 7px;
  top: 0px;
}
</style>
